/**
 * Presentation helpers for Global Sport Mode selection cards.
 * Driven by Sport Registry + match format catalogs — no hardcoded sport lists in UI.
 */

import { resolveSportModule } from "../../sports/registry";
import { normalizeSportId, DEFAULT_SPORT, SPORT_IDS } from "./sportTypes";

/**
 * Fallback emoji by icon token when a module omits `emoji`.
 * Pickleball uses a dedicated glyph (not the table-tennis paddle emoji).
 */
const ICON_EMOJI = Object.freeze({
  shuttle: "🏸",
  ball: null,
  racket: "🎾",
  paddle: "🏓",
  pickleball: null,
});

/**
 * Build a short activity tagline from module catalog (Singles · Doubles · scoring).
 * Modules may override with `selectionTagline`.
 * @param {import("../../sports/SportModule").SportModule | null | undefined} mod
 * @returns {string}
 */
export function buildSportSelectionTagline(mod) {
  if (!mod) return "";
  if (typeof mod.selectionTagline === "string" && mod.selectionTagline.trim()) {
    return mod.selectionTagline.trim();
  }
  const parts = [];
  try {
    const config = mod.createMatchConfiguration?.();
    const scoring = config?.getScoringSystem?.();
    if (scoring === "rally") parts.push("Rally scoring");
    else if (scoring === "side_out") parts.push("Side-out scoring");
    else if (scoring === "itf") parts.push("Game · set scoring");
    const catalog = config?.getCatalog?.() || config?.getDefaultCatalog?.();
    const types = (catalog?.matchTypes || [])
      .map((t) => t?.title)
      .filter(Boolean);
    for (const t of types) {
      if (!parts.includes(t)) parts.push(t);
    }
  } catch {
    /* ignore */
  }
  return parts.join(" · ") || mod.displayName || "";
}

/**
 * @param {string | null | undefined} sportId
 */
export function getSportSelectionPresentation(sportId) {
  const id = normalizeSportId(sportId || DEFAULT_SPORT);
  const mod = resolveSportModule(id);
  const usePickleGlyph =
    id === SPORT_IDS.PICKLEBALL ||
    mod?.icon === "pickleball" ||
    mod?.glyph === "pickleball";
  const emoji = usePickleGlyph
    ? null
    : (typeof mod?.emoji === "string" && mod.emoji) ||
      ICON_EMOJI[mod?.icon] ||
      "🏸";
  return {
    sportId: mod?.id || id,
    displayName: mod?.displayName || id,
    emoji,
    icon: mod?.icon || "shuttle",
    glyph: usePickleGlyph ? "pickleball" : mod?.glyph || null,
    tagline: buildSportSelectionTagline(mod),
    isPlayable: Boolean(mod?.isPlayable),
    module: mod,
  };
}
